import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Target, TrendingUp, DollarSign, CheckCircle2, AlertTriangle, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

export default function BreakEvenCalc({ branchId, token, t, lang, onDataReady }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/api/accounting/break-even?branch_id=${branchId || ''}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(d);
        if (onDataReady) onDataReady(d);
      } catch { toast.error(t('common.failed')); }
      setLoading(false);
    };
    fetch();
  }, [branchId, token, t]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const monthlyPct = Math.min(data.monthly_progress_pct, 100);
  const dailyPct = Math.min(data.daily_progress_pct, 100);
  const reached = data.reached_break_even;

  return (
    <div className="space-y-5" data-testid="break-even-tab">
      {/* Main Break-Even Progress */}
      <Card className={`border-2 ${reached ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
        <CardContent className="p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            {reached ? <CheckCircle2 className="w-6 h-6 text-emerald-500" /> : <Target className="w-6 h-6 text-amber-500" />}
            <h2 className="text-lg font-bold">{reached ? t('breakEven.reached') : t('breakEven.notReached')}</h2>
          </div>
          <p className="text-4xl font-bold mb-2">{fmt(data.break_even_revenue)} <span className="text-base font-normal text-muted-foreground">{t('common.sar')}</span></p>
          <p className="text-sm text-muted-foreground mb-4">{t('breakEven.monthlyTarget')}</p>

          {/* Monthly Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{t('breakEven.progress')}</span>
              <span>{data.monthly_progress_pct}%</span>
            </div>
            <div className="w-full h-4 bg-muted rounded-full overflow-hidden relative">
              <div className={`h-full rounded-full transition-all ${reached ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${monthlyPct}%` }} />
              {/* Break-even marker */}
              <div className="absolute top-0 h-full w-0.5 bg-foreground/50" style={{ left: '100%' }} />
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span className="text-muted-foreground">0</span>
              <span className={`font-medium ${reached ? 'text-emerald-500' : 'text-foreground'}`}>{fmt(data.total_revenue)}</span>
              <span className="text-muted-foreground">{fmt(data.break_even_revenue)}</span>
            </div>
          </div>

          <div className="mt-4">
            <Badge className={`text-sm px-4 py-1 ${reached ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
              {reached ? `+${fmt(data.surplus_or_deficit)} ${t('breakEven.surplus')}` : `${fmt(Math.abs(data.surplus_or_deficit))} ${t('breakEven.deficit')}`}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Daily Break-Even */}
      <Card data-testid="daily-break-even">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm mb-3">{t('breakEven.dailyTarget')}</h3>
          <div className="grid grid-cols-3 gap-4 text-center mb-4">
            <div>
              <p className="text-xs text-muted-foreground">{t('breakEven.dailyNeeded')}</p>
              <p className="text-lg font-bold text-foreground">{fmt(data.daily_break_even)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('breakEven.todayRevenue')}</p>
              <p className="text-lg font-bold text-amber-500">{fmt(data.today_revenue)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('breakEven.dailyProgress')}</p>
              <p className={`text-lg font-bold ${data.daily_progress_pct >= 100 ? 'text-emerald-500' : 'text-amber-500'}`}>{data.daily_progress_pct}%</p>
            </div>
          </div>
          <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${data.daily_progress_pct >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${dailyPct}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Cost Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="fixed-costs">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-red-500" />
              <h3 className="font-semibold text-sm">{t('breakEven.fixedCosts')}</h3>
              <span className="ms-auto text-sm font-bold text-red-500">{fmt(data.total_fixed_costs)}</span>
            </div>
            <div className="space-y-2">
              {Object.entries(data.fixed_costs_breakdown).map(([cat, amt]) => (
                <div key={cat} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t(`accounting.${toCamelCase(cat)}`)}</span>
                  <span className="font-medium">{fmt(amt)}</span>
                </div>
              ))}
              {Object.keys(data.fixed_costs_breakdown).length === 0 && <p className="text-xs text-muted-foreground text-center py-2">{t('breakEven.noFixedCosts')}</p>}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="key-metrics">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-amber-500" />
              <h3 className="font-semibold text-sm">{t('breakEven.keyMetrics')}</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('breakEven.variableCosts')}</span>
                <span className="font-medium">{fmt(data.total_variable_costs)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('breakEven.cmRatio')}</span>
                <span className="font-bold text-amber-500">{data.contribution_margin_ratio}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('breakEven.daysElapsed')}</span>
                <span className="font-medium">{data.days_elapsed} / 30</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
